import { PLAN_LIMITS } from '@repo/domain';

import { config } from './config';

export const landingContent = {
  navbar: {
    links: [
      { label: 'Como funciona', href: '#how-it-works' },
      { label: 'Depoimentos', href: '#testimonials' },
      { label: 'Preços', href: '#pricing' },
      { label: 'FAQ', href: '#faq' },
    ],
    loginLink: 'Entrar',
    cta: 'Começar grátis',
  },

  hero: {
    socialProof: {
      count: 1200,
      label: 'casais usando',
    },
    headline: 'Nunca mais erre um presente',
    subheadline:
      'Crie sua lista de desejos privada. Seu parceiro(a) vê o que você quer. A surpresa continua perfeita.',
    primaryCta: 'Criar minha lista grátis',
    secondaryCta: 'Ver como funciona',
    trustSignals: [
      {
        icon: 'Check',
        text: `Grátis para sempre até ${PLAN_LIMITS.free.maxWishlistItems} itens`,
      },
      { icon: 'Lock', text: 'Privacidade total - só vocês dois têm acesso' },
      { icon: 'Zap', text: 'Comece em 30 segundos' },
    ],
    productImage: '/images/app-screenshot.svg',
  },

  partners: {
    label: 'Casais de todo o Brasil confiam no eumimo',
    logos: [
      { name: 'São Paulo', image: '/images/partners/sp.svg' },
      { name: 'Rio de Janeiro', image: '/images/partners/rj.svg' },
      { name: 'Belo Horizonte', image: '/images/partners/bh.svg' },
      { name: 'Curitiba', image: '/images/partners/ctba.svg' },
      { name: 'Porto Alegre', image: '/images/partners/poa.svg' },
      { name: 'Salvador', image: '/images/partners/ssa.svg' },
    ],
  },

  pain: {
    headline: 'A gente entende a situação',
    points: [
      {
        icon: 'MessageCircleQuestion',
        title: 'Perguntar estraga',
        description: 'Perguntar o que a pessoa quer mata a surpresa',
      },
      {
        icon: 'HelpCircle',
        title: 'Adivinhar dá ansiedade',
        description: 'Escolher sozinho e torcer para acertar',
      },
      {
        icon: 'CircleOff',
        title: 'PIX parece desistência',
        description: 'Mandar dinheiro parece que você desistiu de presentear',
      },
      {
        icon: 'AlertCircle',
        title: 'Data chega sem ideia',
        description: 'A data especial chega e você ainda não sabe o que dar',
      },
    ],
    transition:
      'E se existisse um jeito de acertar sempre, sem perder a magia?',
  },

  solution: {
    headline: 'Uma lista de desejos só de vocês',
    steps: [
      { icon: 'List', text: 'Você registra o que quer ganhar' },
      { icon: 'Users', text: 'Seu parceiro(a) vê a lista' },
      { icon: 'Gift', text: 'Surpresa garantida. Presente certo.' },
    ],
    cta: 'Começar agora - é grátis',
  },

  benefits: {
    headline: 'Por que casais amam o eumimo',
    items: [
      {
        icon: 'Target',
        title: 'Acerte sempre',
        description:
          'Sem adivinhação. Você sabe exatamente o que a pessoa quer.',
        size: 'large' as const,
      },
      {
        icon: 'Sparkles',
        title: 'Surpresa intacta',
        description:
          'A pessoa não sabe o que você escolheu da lista. A magia continua.',
        size: 'small' as const,
      },
      {
        icon: 'Calendar',
        title: 'Nunca esqueça datas',
        description: 'Contagem regressiva para aniversários e datas especiais.',
        size: 'small' as const,
      },
      {
        icon: 'Heart',
        title: 'Sem estresse',
        description:
          'Chega de ansiedade tentando adivinhar o presente perfeito.',
        size: 'small' as const,
      },
      {
        icon: 'Lock',
        title: 'Privacidade total',
        description: 'Só vocês dois têm acesso. Ninguém mais vê suas listas.',
        size: 'small' as const,
      },
      {
        icon: 'Zap',
        title: 'Simples de usar',
        description:
          'Interface minimalista. Sem funções desnecessárias. Feito para ser fácil.',
        size: 'large' as const,
      },
    ],
  },

  howItWorks: {
    id: 'how-it-works',
    headline: '3 passos. 2 minutos. Zero complicação.',
    subheadline:
      'Crie sua lista de desejos, convide seu amor e nunca mais erre um presente.',
    steps: [
      {
        icon: 'CalendarHeart',
        title: 'Cadastre datas e desejos',
        description:
          'Adicione datas especiais e sua lista de presentes. Você recebe um link de login no email.',
        benefits: [
          'Datas sugeridas como Dia dos Namorados e Natal',
          'Adicione itens com nome, link e preço',
          'Sem senha para lembrar',
        ],
      },
      {
        icon: 'LogIn',
        title: 'Acesse sua conta',
        description: 'Clique no link enviado por email e entre na sua conta.',
        benefits: [
          'Login seguro por email',
          'Acesso instantâneo à sua lista',
          'Funciona em qualquer dispositivo',
        ],
      },
      {
        icon: 'Heart',
        title: 'Convide seu amor',
        description: 'Seu parceiro(a) faz o mesmo. Pronto para presentear!',
        benefits: [
          'Convite enviado por email',
          'Cada um vê a lista do outro',
          'Surpresa garantida sempre',
        ],
      },
    ],
    cta: 'Começar agora',
  },

  pricing: {
    id: 'pricing',
    headline: 'Comece grátis. Cresça se quiser.',
    subheadline: 'Sem surpresas. Sem taxas escondidas.',
    billingToggle: {
      monthly: 'Mensal',
      yearly: 'Anual',
    },
    plans: [
      {
        name: 'Grátis',
        price: 'R$0',
        priceDetail: 'para sempre',
        badge: null,
        features: [
          `Até ${PLAN_LIMITS.free.maxWishlistItems} itens na lista de desejos`,
          `Até ${PLAN_LIMITS.free.maxOccasions} datas comemorativas`,
          'Suporte por email',
        ],
        limitations: [],
        cta: 'Começar grátis',
        ctaVariant: 'outline' as const,
        highlighted: false,
      },
      {
        name: 'Pro',
        monthlyPrice: 'R$9',
        monthlyPriceDetail: '/mês',
        yearlyPrice: 'R$49',
        yearlyPriceDetail: '/ano',
        yearlySavings: 'economia de 55%',
        badge: 'Mais popular',
        features: [
          'Itens ilimitados',
          'Datas ilimitadas',
          'Suporte prioritário no WhatsApp',
        ],
        limitations: [],
        cta: 'Assinar Pro',
        ctaVariant: 'default' as const,
        highlighted: true,
      },
    ],
  },

  testimonials: {
    id: 'testimonials',
    headline: 'O que casais estão dizendo',
    items: [
      {
        avatar:
          'https://ui-avatars.com/api/?name=Marina+Pedro&background=random&size=200',
        name: 'Marina e Pedro',
        context: 'Juntos há 5 anos',
        rating: 5,
        quote:
          'Nunca mais erramos um presente. Agora toda data especial é perfeita!',
      },
      {
        avatar:
          'https://ui-avatars.com/api/?name=Julia+Marcos&background=random&size=200',
        name: 'Julia e Marcos',
        context: 'Casados há 2 anos',
        rating: 5,
        quote:
          'A melhor parte é que a surpresa continua. Ele não sabe qual item eu escolhi!',
      },
      {
        avatar:
          'https://ui-avatars.com/api/?name=Camila+Rafael&background=random&size=200',
        name: 'Camila e Rafael',
        context: 'Namorando há 1 ano',
        rating: 5,
        quote:
          'Simples, bonito e funciona. Exatamente o que a gente precisava.',
      },
      {
        avatar:
          'https://ui-avatars.com/api/?name=Ana+Lucas&background=random&size=200',
        name: 'Ana e Lucas',
        context: 'Juntos há 8 anos',
        rating: 5,
        quote:
          'Depois de tantos anos juntos, ainda conseguimos nos surpreender. Obrigada eumimo!',
      },
      {
        avatar:
          'https://ui-avatars.com/api/?name=Beatriz+Thiago&background=random&size=200',
        name: 'Beatriz e Thiago',
        context: 'Noivos',
        rating: 5,
        quote: 'Usamos para o chá de panela também. Todo mundo amou a ideia!',
      },
      {
        avatar:
          'https://ui-avatars.com/api/?name=Fernanda+Diego&background=random&size=200',
        name: 'Fernanda e Diego',
        context: 'Casados há 10 anos',
        rating: 5,
        quote:
          'Chega de presentes errados! Agora sempre acertamos e a surpresa continua.',
      },
    ],
  },

  faq: {
    id: 'faq',
    headline: 'Perguntas frequentes',
    items: [
      {
        question: 'Meu parceiro(a) vê o que eu vou comprar?',
        answer:
          'Não. Você vê a lista dele(a), mas a pessoa nunca sabe qual item você escolheu. A surpresa está garantida.',
      },
      {
        question: 'E se a gente terminar?',
        answer:
          'Seus dados são seus. Você pode desvincular o relacionamento a qualquer momento. Nenhuma informação é compartilhada após isso.',
      },
      {
        question: 'Meus dados estão seguros?',
        answer:
          'Sim. Usamos criptografia padrão de mercado. Só você e seu parceiro(a) têm acesso às listas. Nós não vendemos dados.',
      },
      {
        question: 'Preciso pagar para usar?',
        answer: `Não. O plano grátis inclui ${PLAN_LIMITS.free.maxWishlistItems} itens e ${PLAN_LIMITS.free.maxOccasions} datas - suficiente para a maioria dos casais. Pro é opcional para quem quer mais.`,
      },
      {
        question: 'É complicado de usar?',
        answer:
          'Não. Login sem senha, interface minimalista, sem funções desnecessárias. Feito para ser simples.',
      },
      {
        question: 'Como cancelo minha assinatura?',
        answer:
          'Você pode cancelar a qualquer momento nas configurações da conta. Sem burocracia, sem perguntas.',
      },
      {
        question: 'Tem reembolso?',
        answer:
          'Sim. Se não estiver satisfeito nos primeiros 7 dias, devolvemos seu dinheiro integralmente.',
      },
    ],
  },

  finalCta: {
    headline: 'O próximo presente pode ser perfeito',
    subheadline:
      'Crie sua lista de desejos em 30 segundos. Convide seu amor. Nunca mais erre.',
    cta: 'Criar minha lista grátis',
    trust: `Grátis para sempre até ${PLAN_LIMITS.free.maxWishlistItems} itens. Sem cartão de crédito.`,
  },

  footer: {
    description:
      'A lista de desejos de casais. Acerte sempre, surpreenda sempre.',
    menu: {
      title: 'Menu',
      links: [
        { label: 'Como funciona', href: '#how-it-works' },
        { label: 'Preços', href: '#pricing' },
        { label: 'FAQ', href: '#faq' },
      ],
    },
    legal: {
      title: 'Legal',
      cnpj: config.company.cnpj,
      links: [
        { label: 'Termos de Uso', href: '/terms' },
        { label: 'Política de Privacidade', href: '/privacy' },
      ],
    },
    social: {
      title: 'Social',
      links: [
        { label: 'Instagram', href: 'https://instagram.com/eumimo' },
        { label: 'TikTok', href: 'https://tiktok.com/@eumimo' },
      ],
    },
  },
} as const;
